import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Save, RotateCcw, ChevronDown, ChevronUp, Video, MessageSquare,
  Star, HelpCircle, BarChart2, Tv, ArrowRight, Phone, CheckCircle2,
  AlertCircle, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { useSiteContent } from "../../hooks/useSiteContent";
import { DEFAULTS } from "../../hooks/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// ─── Seções do painel ───────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "hero",
    label: "Hero (Capa)",
    icon: Tv,
    color: "text-amber-400",
    fields: [
      { key: "hero_title_line1", label: "Título - Linha 1", type: "text" },
      { key: "hero_title_line2", label: "Título - Linha 2 (destaque dourado)", type: "text" },
      { key: "hero_subtitle", label: "Subtítulo / Descrição", type: "textarea" },
      { key: "hero_cta_text", label: "Texto do botão CTA", type: "text" },
      { key: "hero_badge_text", label: "Texto do badge (credenciais)", type: "text" },
      { key: "hero_portfolio_value", label: "Valor da carteira (ex: R$ 13M)", type: "text" },
    ],
  },
  {
    id: "about",
    label: "Sobre Mim",
    icon: MessageSquare,
    color: "text-blue-400",
    fields: [
      { key: "about_video_src", label: "Caminho do vídeo (ex: /videos/MeuVideo.mp4)", type: "text", hint: "Coloque o arquivo em frontend/public/videos/" },
      { key: "about_title", label: "Título principal", type: "text" },
      { key: "about_title_highlight", label: "Destaque dourado do título", type: "text" },
      { key: "about_p1", label: "Parágrafo 1", type: "textarea" },
      { key: "about_p2", label: "Parágrafo 2", type: "textarea" },
      { key: "about_p3", label: "Parágrafo 3", type: "textarea" },
      { key: "about_p4", label: "Parágrafo 4", type: "textarea" },
      { key: "about_p5", label: "Frase de destaque final", type: "text" },
      { key: "about_stat1_value", label: "Estatística 1 — Valor", type: "text" },
      { key: "about_stat1_label", label: "Estatística 1 — Legenda", type: "text" },
      { key: "about_stat2_value", label: "Estatística 2 — Valor", type: "text" },
      { key: "about_stat2_label", label: "Estatística 2 — Legenda", type: "text" },
      { key: "about_stat3_value", label: "Estatística 3 — Valor", type: "text" },
      { key: "about_stat3_label", label: "Estatística 3 — Legenda", type: "text" },
    ],
  },
  {
    id: "stats",
    label: "Estatísticas",
    icon: BarChart2,
    color: "text-green-400",
    fields: [
      { key: "stat1_value", label: "Stat 1 — Valor", type: "text" },
      { key: "stat1_label", label: "Stat 1 — Legenda", type: "text" },
      { key: "stat2_value", label: "Stat 2 — Valor", type: "text" },
      { key: "stat2_label", label: "Stat 2 — Legenda", type: "text" },
      { key: "stat3_value", label: "Stat 3 — Valor", type: "text" },
      { key: "stat3_label", label: "Stat 3 — Legenda", type: "text" },
      { key: "stat4_value", label: "Stat 4 — Valor", type: "text" },
      { key: "stat4_label", label: "Stat 4 — Legenda", type: "text" },
    ],
  },
  {
    id: "video",
    label: "Seção de Vídeo",
    icon: Video,
    color: "text-purple-400",
    fields: [
      { key: "video_src", label: "Caminho do vídeo (ex: /videos/MeuVideo.mp4)", type: "text", hint: "Coloque o arquivo em frontend/public/videos/" },
      { key: "video_title", label: "Título — Parte 1", type: "text" },
      { key: "video_title_highlight", label: "Título — Parte destaque (dourado)", type: "text" },
      { key: "video_subtitle", label: "Título — Continuação", type: "text" },
      { key: "video_description", label: "Descrição / Texto explicativo", type: "textarea" },
    ],
  },
  {
    id: "testimonials",
    label: "Depoimentos",
    icon: Star,
    color: "text-yellow-400",
    fields: [
      { key: "testimonial1_name", label: "Depoimento 1 — Nome", type: "text" },
      { key: "testimonial1_role", label: "Depoimento 1 — Cargo/Cidade", type: "text" },
      { key: "testimonial1_text", label: "Depoimento 1 — Texto", type: "textarea" },
      { key: "testimonial2_name", label: "Depoimento 2 — Nome", type: "text" },
      { key: "testimonial2_role", label: "Depoimento 2 — Cargo/Cidade", type: "text" },
      { key: "testimonial2_text", label: "Depoimento 2 — Texto", type: "textarea" },
      { key: "testimonial3_name", label: "Depoimento 3 — Nome", type: "text" },
      { key: "testimonial3_role", label: "Depoimento 3 — Cargo/Cidade", type: "text" },
      { key: "testimonial3_text", label: "Depoimento 3 — Texto", type: "textarea" },
      { key: "testimonial4_name", label: "Depoimento 4 — Nome", type: "text" },
      { key: "testimonial4_role", label: "Depoimento 4 — Cargo/Cidade", type: "text" },
      { key: "testimonial4_text", label: "Depoimento 4 — Texto", type: "textarea" },
    ],
  },
  {
    id: "faq",
    label: "FAQ (Perguntas)",
    icon: HelpCircle,
    color: "text-cyan-400",
    fields: [
      { key: "faq1_q", label: "Pergunta 1", type: "text" },
      { key: "faq1_a", label: "Resposta 1", type: "textarea" },
      { key: "faq2_q", label: "Pergunta 2", type: "text" },
      { key: "faq2_a", label: "Resposta 2", type: "textarea" },
      { key: "faq3_q", label: "Pergunta 3", type: "text" },
      { key: "faq3_a", label: "Resposta 3", type: "textarea" },
      { key: "faq4_q", label: "Pergunta 4", type: "text" },
      { key: "faq4_a", label: "Resposta 4", type: "textarea" },
      { key: "faq5_q", label: "Pergunta 5", type: "text" },
      { key: "faq5_a", label: "Resposta 5", type: "textarea" },
      { key: "faq6_q", label: "Pergunta 6", type: "text" },
      { key: "faq6_a", label: "Resposta 6", type: "textarea" },
      { key: "faq7_q", label: "Pergunta 7", type: "text" },
      { key: "faq7_a", label: "Resposta 7", type: "textarea" },
      { key: "faq8_q", label: "Pergunta 8", type: "text" },
      { key: "faq8_a", label: "Resposta 8", type: "textarea" },
    ],
  },
  {
    id: "global",
    label: "Configurações Globais",
    icon: Phone,
    color: "text-rose-400",
    fields: [
      { key: "cta_button_text", label: "Texto do botão CTA (global)", type: "text" },
      { key: "whatsapp_number", label: "Número WhatsApp (ex: 5551999999999)", type: "text", hint: "Código do país + DDD + número, sem espaços ou traços" },
      { key: "whatsapp_message", label: "Mensagem pré-definida do WhatsApp", type: "textarea" },
    ],
  },
];

// ─── Campo de edição ─────────────────────────────────────────────────────────
function FieldEditor({ field, value, defaultValue, onChange, onReset }) {
  const isModified = value !== defaultValue;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          {field.label}
          {isModified && (
            <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full font-mono">editado</span>
          )}
        </label>
        {isModified && (
          <button
            onClick={() => onReset(field.key)}
            className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            title="Restaurar valor padrão"
          >
            <RotateCcw size={10} /> padrão
          </button>
        )}
      </div>
      {field.type === "textarea" ? (
        <textarea
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          rows={4}
          className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground resize-y focus:outline-none focus:border-primary/60 transition-colors"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/60 transition-colors"
        />
      )}
      {field.hint && (
        <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1">
          <AlertCircle size={10} /> {field.hint}
        </p>
      )}
    </div>
  );
}

// ─── Seção colapsável ─────────────────────────────────────────────────────────
function SectionEditor({ section, localContent, defaultContent, onChange, onReset, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;
  const modifiedCount = section.fields.filter(
    (f) => localContent[f.key] !== defaultContent[f.key]
  ).length;

  return (
    <div className="border border-border/60 rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-foreground/[0.03] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={16} className={section.color} />
          <span className="font-display text-sm font-medium">{section.label}</span>
          {modifiedCount > 0 && (
            <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded-full font-mono">
              {modifiedCount} alteração{modifiedCount > 1 ? "ões" : ""}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-2 border-t border-border/40 space-y-4">
              {section.fields.map((field) => (
                <FieldEditor
                  key={field.key}
                  field={field}
                  value={localContent[field.key] ?? defaultContent[field.key]}
                  defaultValue={defaultContent[field.key]}
                  onChange={onChange}
                  onReset={onReset}
                />
              ))}
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => onSave(section.fields.map((f) => f.key))}
                  disabled={saving || modifiedCount === 0}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-primary-foreground text-sm font-medium shadow-gold transition-all hover:translate-y-[-1px] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "var(--gold-gradient)" }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Salvar {section.label}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Tab principal ────────────────────────────────────────────────────────────
export default function ContentTab({ adminPassword }) {
  const { content: remoteContent, refresh } = useSiteContent();
  const [localContent, setLocalContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sincroniza o estado local quando o conteúdo remoto muda
  useEffect(() => {
    setLocalContent({ ...DEFAULTS, ...remoteContent });
  }, [remoteContent]);

  const handleChange = useCallback((key, value) => {
    setLocalContent((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleReset = useCallback(async (key) => {
    try {
      await axios.delete(`${API}/admin/site-content/${key}`, {
        headers: { "x-admin-password": adminPassword },
      });
      setLocalContent((prev) => ({ ...prev, [key]: DEFAULTS[key] }));
      await refresh();
      toast.success("Campo restaurado ao valor padrão.");
    } catch (err) {
      toast.error("Erro ao restaurar campo.");
    }
  }, [adminPassword, refresh]);

  const handleSave = useCallback(async (keys) => {
    setSaving(true);
    try {
      const updates = {};
      keys.forEach((k) => {
        updates[k] = localContent[k] ?? DEFAULTS[k];
      });
      await axios.put(`${API}/admin/site-content`, updates, {
        headers: { "x-admin-password": adminPassword },
      });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast.success("Conteúdo salvo com sucesso! O site já foi atualizado.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar conteúdo.");
    } finally {
      setSaving(false);
    }
  }, [localContent, adminPassword, refresh]);

  const handleSaveAll = useCallback(() => {
    handleSave(Object.keys(DEFAULTS));
  }, [handleSave]);

  const totalModified = Object.keys(DEFAULTS).filter(
    (k) => localContent[k] !== DEFAULTS[k]
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-light">Conteúdo do Site</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Edite textos, vídeos e configurações. As alterações aparecem no site imediatamente após salvar.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {saved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-1.5 text-green-400 text-sm"
            >
              <CheckCircle2 size={14} /> Salvo!
            </motion.div>
          )}
          {totalModified > 0 && (
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-primary-foreground text-sm font-medium shadow-gold transition-all hover:translate-y-[-1px] disabled:opacity-50"
              style={{ background: "var(--gold-gradient)" }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salvar tudo ({totalModified})
            </button>
          )}
        </div>
      </div>

      {/* Aviso de como funciona */}
      <div className="bg-primary/5 border border-primary/20 rounded-sm px-5 py-4 flex gap-3 items-start">
        <ArrowRight size={14} className="text-primary mt-0.5 flex-shrink-0" />
        <div className="text-sm text-muted-foreground">
          <span className="text-foreground font-medium">Como funciona:</span> edite qualquer campo abaixo e clique em{" "}
          <strong className="text-foreground">Salvar</strong>. As mudanças são armazenadas no banco de dados e aparecem
          no site imediatamente. Para voltar ao texto original, clique em{" "}
          <strong className="text-foreground">padrão</strong> ao lado do campo.
        </div>
      </div>

      {/* Seções */}
      <div className="space-y-3">
        {SECTIONS.map((section) => (
          <SectionEditor
            key={section.id}
            section={section}
            localContent={localContent}
            defaultContent={DEFAULTS}
            onChange={handleChange}
            onReset={handleReset}
            onSave={handleSave}
            saving={saving}
          />
        ))}
      </div>
    </div>
  );
}
